import { PageHeader } from "@shared/components/PageHeader";
import { FormField, FormService } from "@shared/services/FormServices";
import { SharedFormArray } from "@shared/components/SharedFormArray";
import React from "react";
import { Button } from "@shared/ui/button";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@shared/hooks/use-toast";
import {
  useChoices,
  useCreateGlCodes,
  useGlSources,
  useGlCodeDetails,
  useUpdateGlCodesMulti,
} from "../../hooks";
import { getTransactionCategories, getTransactionTypes } from "../../api";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

const CreateGlCode = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { action, id, glCode } = useParams<{
    action: string;
    id: string;
    glCode: string;
  }>();
  const isEditMode = action === "edit";

  const { data: details, isLoading: isDetailsLoading } = useGlCodeDetails(
    isEditMode ? glCode : undefined,
  );

  const { mutateAsync: createGlCodesMutation, isPending: isCreatePending } =
    useCreateGlCodes();
  const {
    mutateAsync: updateGlCodesMultiMutation,
    isPending: isUpdatePending,
  } = useUpdateGlCodesMulti();

  const isPending = isCreatePending || isUpdatePending;

  // Options cached by parent value to support multiple rows
  const [categoriesBySource, setCategoriesBySource] = React.useState<
    Record<string, any[]>
  >({});
  const [typesByCategory, setTypesByCategory] = React.useState<
    Record<string, any[]>
  >({});

  const { data: glTypesData } = useChoices("gl-types");
  const { data: sourcesData } = useGlSources();

  const glTypesOptions = React.useMemo(
    () =>
      (glTypesData || []).map((choice: any) => ({
        value: choice.value,
        label: choice.label,
      })),
    [glTypesData],
  );

  const sourcesOptions = React.useMemo(
    () =>
      (sourcesData || []).map((choice: any) => ({
        value: choice.value,
        label: choice.label,
      })),
    [sourcesData],
  );

  // End of static options

  const itemFields: FormField[] = React.useMemo(
    () => [
      {
        name: "glType",
        label: "GL Type",
        type: "select",
        required: true,
        placeholder: "- Select -",
        options: glTypesOptions,
      },
      {
        name: "source",
        label: "Source",
        type: "select",
        required: true,
        placeholder: "- Select -",
        options: sourcesOptions,
      },
      {
        name: "transactionCategory",
        label: "Transaction Category",
        type: "select",
        required: true,
        placeholder: "- Select Category -",
        getOptions: (rowData: any) => {
          return rowData?.source
            ? categoriesBySource[rowData.source] || []
            : [];
        },
      },
      {
        name: "transactionType",
        label: "Transaction Type",
        type: "select",
        required: false,
        placeholder: "- Select Type -",
        getOptions: (rowData: any) => {
          return rowData?.transactionCategory
            ? typesByCategory[rowData.transactionCategory] || []
            : [];
        },
      },
      {
        name: "glCode",
        label: "GL Code",
        type: "text",
        required: true,
        placeholder: "e.g. 4610",
      },
      {
        name: "glName",
        label: "GL Name",
        type: "text",
        required: true,
        placeholder: "e.g. Solar Energy Revenue",
      },
    ],
    [
      glTypesOptions,
      sourcesOptions,
      categoriesBySource,
      typesByCategory,
      isEditMode,
    ],
  );

  const form = FormService.createForm({
    fields: [{ name: "glCodes", type: "form-array", itemFields: itemFields }],
    mode: "onChange",
  });

  const dataLoadedRef = React.useRef(false);

  React.useEffect(() => {
    if (dataLoadedRef.current) return;

    switch (action) {
      case "edit":
        if (details) {
          let sourceVal: string | undefined;
          let catVal: string | undefined;
          let typeVal: string | undefined;
          let editData: any;

          const rowsToUse = details.rawRows || details.rows;

          if (rowsToUse && rowsToUse.length > 0) {
            rowsToUse.forEach((row: any) => {
              const src = row.source?.toString();
              const cat = row.transactionCategory?.toString();

              if (src) {
                getTransactionCategories(src)
                  .then((data) => {
                    setCategoriesBySource((prev) => ({
                      ...prev,
                      [src]: data.map((choice: any) => ({
                        value: choice.value,
                        label: choice.label,
                      })),
                    }));
                  })
                  .catch((e) => console.error("Failed to load categories", e));
              }

              if (cat) {
                getTransactionTypes(cat)
                  .then((data) => {
                    setTypesByCategory((prev) => ({
                      ...prev,
                      [cat]: data.map((choice: any) => ({
                        value: choice.value || choice,
                        label: choice.label || choice,
                      })),
                    }));
                  })
                  .catch((e) => console.error("Failed to load types", e));
              }
            });

            editData = {
              glCodes: rowsToUse.map((row: any) => ({
                id: row.id,
                glCode: details.glCode,
                glName: details.glName,
                glType: row.glType?.toString(),
                source: row.source?.toString(),
                transactionCategory: row.transactionCategory?.toString(),
                transactionType: row.transactionType?.toString(),
                status: row.status?.toString(),
              })),
            };
          } else {
            const detailsSources = details.sources || details.source;
            const detailsCats = details.transactionCategories;
            const detailsTypes = details.transactionTypes;

            const singleSrc = detailsSources?.[0]?.value?.toString();
            const singleCat = detailsCats?.[0]?.value?.toString();
            const singleType =
              detailsTypes?.[0]?.value?.toString() ||
              detailsTypes?.[0]?.toString();

            if (singleSrc && detailsCats) {
              setCategoriesBySource((prev) => ({
                ...prev,
                [singleSrc]: detailsCats.map((choice: any) => ({
                  value: choice.value,
                  label: choice.label,
                })),
              }));
            }

            if (singleCat && detailsTypes) {
              setTypesByCategory((prev) => ({
                ...prev,
                [singleCat]: detailsTypes.map((choice: any) => ({
                  value: choice.value || choice,
                  label: choice.label || choice,
                })),
              }));
            }

            editData = {
              glCodes: [
                {
                  glCode: details.glCode,
                  glName: details.glName,
                  glType: details.glType?.toString(),
                  source: singleSrc,
                  transactionCategory: singleCat,
                  transactionType: singleType,
                },
              ],
            };
          }

          form.reset(editData);
          dataLoadedRef.current = true;
        }
        break;
      default:
        dataLoadedRef.current = true;
        break;
    }
  }, [action, details, form]);

  const handleFieldChange = async (name: string, value: any, index: number) => {
    const strVal = value;
    switch (name) {
      case "source": {
        // Clear dependent fields in this row since source changed
        form.setValue(`glCodes.${index}.transactionCategory`, "");
        form.setValue(`glCodes.${index}.transactionType`, "");

        if (strVal && !categoriesBySource[strVal]) {
          try {
            const data = await getTransactionCategories(strVal);
            setCategoriesBySource((prev) => ({
              ...prev,
              [strVal]: data.map((choice: any) => ({
                value: choice.value,
                label: choice.label,
              })),
            }));
          } catch (e) {
            console.error("Failed to fetch transaction categories", e);
          }
        }
        break;
      }
      case "transactionCategory": {
        // Clear dependent field in this row since category changed
        form.setValue(`glCodes.${index}.transactionType`, "");

        if (strVal && !typesByCategory[strVal]) {
          try {
            const data = await getTransactionTypes(strVal);
            setTypesByCategory((prev) => ({
              ...prev,
              [strVal]: data.map((choice: any) => ({
                value: choice.value || choice,
                label: choice.label || choice,
              })),
            }));
          } catch (e) {
            console.error("Failed to fetch transaction types", e);
          }
        }
        break;
      }
      default:
        break;
    }
  };

  const onSubmit = async () => {
    const isValid = await FormService.validateForm(form);
    if (!isValid) return;

    const data = form.getValues();
    const glCodes = data.glCodes || [];

    let hasErrors = false;
    let hasGlCodeMismatch = false;
    const codeMap = new Map<string, string>(); // maps glCode -> glName

    glCodes.forEach((row: any, index: number) => {
      // Cross-row validation for GL Code and GL Name
      if (row.glCode && row.glName) {
        if (codeMap.has(row.glCode)) {
          if (codeMap.get(row.glCode) !== row.glName) {
            hasGlCodeMismatch = true;
            hasErrors = true;
          }
        } else {
          codeMap.set(row.glCode, row.glName);
        }
      }
    });

    if (hasGlCodeMismatch) {
      toast({
        title: "Error",
        description: "GL Code already used with a different name",
        variant: "destructive",
      });
    }

    if (!hasErrors) {
      try {
        switch (action) {
          case "edit": {
            const rows = glCodes.map((row: any) => ({
              id: row.id || null, // Include id for edit mode
              gl_code: row.glCode,
              gl_name: row.glName,
              gl_type: row.glType ? Number(row.glType) : null,
              source: row.source ? Number(row.source) : null,
              transaction_category: row.transactionCategory
                ? Number(row.transactionCategory)
                : null,
              transaction_type: row.transactionType,
              status:
                row.status !== undefined
                  ? Number(row.status)
                  : (details?.status ?? 1),
            }));

            const payload = {
              remote_utility_id: getRemoteUtilityId(),
              rows,
            };

            await updateGlCodesMultiMutation(payload);
            navigate("/bx/gl-code");
            break;
          }
          default: {
            const rows = glCodes.map((row: any) => ({
              gl_code: row.glCode,
              gl_name: row.glName,
              gl_type: row.glType ? Number(row.glType) : null,
              source: row.source ? Number(row.source) : null,
              transaction_category: row.transactionCategory
                ? Number(row.transactionCategory)
                : null,
              transaction_type: row.transactionType,
              status: 1,
            }));

            const payload = {
              remote_utility_id: getRemoteUtilityId(),
              rows,
            };

            await createGlCodesMutation(payload);
            navigate("/bx/gl-code");
            break;
          }
        }
      } catch (error) {
        // Error is handled by the hook
      }
    }
  };

  return (
    <div className="p-2">
      <div className="flex flex-col xl:flex-row justify-between xl:items-center gap-8 mb-6">
        <div className="flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/bx/gl-code")}
          />
          <div className="flex flex-col">
            <PageHeader
              title={isEditMode ? "Edit GL Code" : "Add Multiple GL Codes"}
              subtitle={
                isEditMode
                  ? "Edit the details of the selected GL Code."
                  : "Fill in each row and click Add Row to add more. All rows are saved together."
              }
            />
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            variant="default"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isPending ? "Saving..." : "Save GL Codes"}
          </Button>
        </div>
      </div>

      <SharedFormArray
        name="glCodes"
        form={form}
        itemFields={itemFields}
        variant="table"
        addButtonText="Add Row"
        minItems={1}
        maxItems={isEditMode ? 1 : undefined}
        cloneable={!isEditMode}
        fieldsToClearOnClone={[]}
        onFieldChange={handleFieldChange}
        footerText="Note: GL Code and GL Name are required. The same GL Code can appear in multiple rows (different transactions) — but all rows with the same GL Code must share the same GL Name. If the names differ, a mismatch error is shown and saving is blocked until resolved."
      />
    </div>
  );
};

export default CreateGlCode;
